import "./GamePage.style.css";

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GameTable } from "./GameTable/GameTable";
import { GameObjectState } from "../../utils/states/GamePageStates/GameObjectState";
import { GamePropertyState } from "../../utils/states/GamePageStates/GamePropertyState";
import { ApiFactory } from "../../api-client/api-factory";
import { GamesApi } from "../../api-client";
import { PropertiesMapper } from "../../utils/mappers/PropertiesMapper";
import { GameAnswerState } from "../../utils/states/GamePageStates/GameAnswerState";
import { GameObjectMapper } from "../../utils/mappers/GameObjectMapper";
import { UniqueId } from "../../utils/UniqueId";
import { VictoryModal } from "./VictoryModal/VictoryModal";
import { AppRouter } from "../../utils/enums/AppRouter";
import { GameDropdown } from "../../components/special/GameDropdown/GameDropdown";
import { IconTools18 } from "../../components/icons/IconTools/IconTools18";
import { Button, ButtonColor } from "../../components/base/Button/Button";
import { DoorExitIcon24 } from "../../components/icons/DoorExitIcon24";
import { useIsMobile } from "../../utils/functions/useIsMobile";
import { IconTools24 } from "../../components/icons/IconTools/IconTools24";

interface GameInfo {
  gameName: string;
  gameAuthor: string;
}

interface GamePageProps {
  showAuthor?: boolean;
}

export const GamePage = ({ showAuthor = true }: GamePageProps) => {
  const { gameId } = useParams();

  const [searchParams] = useSearchParams();
  const isSolo = searchParams.get("solo") === "true";
  const userId = searchParams.get("userId");
  const confId = searchParams.get("confId");
  const activityId = searchParams.get("activityId");
  const appUrl = searchParams.get("appUrl");
  const isMobile = useIsMobile();

  const navigate = useNavigate();

  const [isDataReceived, setIsDataReceived] = useState<boolean>(false);
  const [isFullGuessed, setIsFullGuessed] = useState<boolean>(false);
  const [isShowingDialogue, setIsShowingDialogue] = useState<boolean>(false);

  const [objects, setObjects] = useState<GameObjectState[]>([]);
  const [properties, setProperties] = useState<GamePropertyState[]>([]);
  const [gameAnswerStates, setGameAnswerStates] = useState<GameAnswerState[]>(
    [],
  );
  const [guessedObject, setGuessedObject] = useState<GameAnswerState | null>(
    null,
  );

  const [gameInfo, setGameInfo] = useState<GameInfo>({
    gameName: "",
    gameAuthor: "",
  });

  const [gameSessionId, setGameSessionId] = useState<string>();

  useEffect(() => {
    if (gameId && !isDataReceived) {
      UniqueId.clear();

      ApiFactory.get(GamesApi)
        .gamesGameIdFullGet(gameId)
        .then((data) => {
          setGameInfo({
            gameName: data.game?.name!,
            gameAuthor: data.game?.creator?.name!,
          });

          const newObjects = GameObjectMapper.toGameObjectStates(
            data.game?.collection?.objects!,
          );
          setObjects(newObjects);

          const newProperties = PropertiesMapper.toGamePropertyStates(
            data.game?.collection?.fields!,
          );
          setProperties(newProperties);

          setGameSessionId(data.gameSessionId!);

          setIsDataReceived(true);
        })
        .catch(() => {
          console.error("Игра не найдена");
          navigate(AppRouter.Gallery);
        });
    }
  }, [gameId, isDataReceived, navigate]);

  useEffect(() => {
    if (isFullGuessed) {
      setTimeout(() => {
        setIsShowingDialogue(true);
      }, 2500);
    }
  }, [isFullGuessed]);

  const handleDropdownItemSelected = (selectedObject: GameObjectState) => {
    if (gameId) {
      const tempQueryParams = {
        userId: userId,
        confId: confId,
        activityId: activityId,
        appUrl: appUrl,
      };

      const filteredQueryKeys = Object.keys(tempQueryParams).filter(
        (key) =>
          tempQueryParams[key as keyof typeof tempQueryParams] !== null &&
          tempQueryParams[key as keyof typeof tempQueryParams] !== undefined,
      );

      const queryParams: { [key: string]: any } = {};
      filteredQueryKeys.forEach((key) => {
        queryParams[key] = tempQueryParams[key as keyof typeof tempQueryParams];
      });

      const options: { [key: string]: any } = {};
      if (Object.keys(queryParams).length > 0) {
        options.query = queryParams;
      }

      ApiFactory.get(GamesApi)
        .gamesGameIdGuessObjectIdPost(
          gameId,
          selectedObject.apiId,
          gameSessionId!,
          options,
        )
        .then((data) => {
          if (gameSessionId !== data.gameSessionId) {
            setGameSessionId(data.gameSessionId!);
          }
          const newGameAnswerState = GameObjectMapper.toGameAnswerState(
            selectedObject,
            data.fieldGuesses!,
            properties,
            data.isFullGuess!,
          );
          setGameAnswerStates((prevState) => [
            ...prevState,
            newGameAnswerState,
          ]);
          setIsFullGuessed(data.isFullGuess!);
          if (data.isFullGuess!) {
            setGuessedObject(newGameAnswerState);
          }
        })
        .catch(() => console.error("Не получилось отправить попытку"));
    }

    setObjects((prevState) =>
      prevState.map((object) =>
        object.id === selectedObject.id ? { ...object, isTried: true } : object,
      ),
    );
  };

  const handleCloseDialogue = () => {
    setIsShowingDialogue(false);
  };

  const handleAddToFavorites = () => {
    ApiFactory.get(GamesApi)
      .gamesGameIdSetFavouriteSetFavouritePatch(gameId!, true)
      .then(() => console.log("Игра добавлена в избранное!"))
      .catch(() =>
        console.error("ERR: Не получилось добавить игру в избранное"),
      );
  };

  const renderContent = () => {
    return (
      <div className="game-page">
        {isDataReceived && (
          <main className="game-page__content">
            <div className="game-page__game-header">
              <div className="game-page__game-header__name">
                {gameInfo.gameName}
              </div>
              {showAuthor && (
                <div className="game-page__game-header__author">
                  {isMobile ? (
                    <IconTools18 color="#BF00E5" />
                  ) : (
                    <IconTools24 color="#BF00E5" />
                  )}
                  <span>{gameInfo.gameAuthor}</span>
                </div>
              )}
            </div>
            <div className="game-page__game-body">
              <GameDropdown
                className="game-page__game-body__input"
                items={objects.filter((object) => !object.isTried)}
                showImages={!isSolo}
                name="game-objects"
                onItemSelected={handleDropdownItemSelected}
                disabled={isFullGuessed}
              />
              <GameTable
                properties={properties}
                gameAnswerStates={gameAnswerStates}
              />
            </div>
          </main>
        )}
        {!isSolo && (
          <Button
            className="game-page__exit-button"
            icon={<DoorExitIcon24 />}
            text="Выйти из игры"
            color={ButtonColor.Border}
            onClick={() => navigate(AppRouter.Gallery)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="game-page__wrapper">
      {renderContent()}
      {isShowingDialogue && (
        <div className="game-page__modal-overlay">
          <VictoryModal
            gameId={gameId!}
            objectName={guessedObject?.name!}
            gameName={gameInfo.gameName}
            gameAnswerStates={gameAnswerStates}
            handleCloseDialogue={handleCloseDialogue}
            handleAddToFavorites={handleAddToFavorites}
          />
        </div>
      )}
    </div>
  );
};
